/**
 * Verificação automatizada do gerador de exercícios rítmicos.
 *
 * Este módulo é 100% puro (não usa React, DOM nem áudio), então roda tanto no
 * navegador — através da página interna de diagnóstico (/diagnostico) — quanto
 * fora dele, com um runner de TypeScript (ex.: `tsx`).
 *
 * O que ele confere (invariantes do motor):
 *   1. A soma das durações de cada compasso bate exatamente com a fórmula
 *      (2/4, 3/4 e 4/4), sem sobreposição nem buraco vazio.
 *   2. Cada figura desenhada corresponde à duração declarada (`durationId`).
 *   3. Cenários específicos: exercício terminando em PAUSA e em LIGADURA.
 *
 * Contagem por fórmula: cada compasso (2/4, 3/4, 4/4) tem o seu próprio
 * contador de casos gerados e inválidos. A contagem de inválidos é independente
 * do limite de mensagens guardadas.
 *
 * Execução em etapas: `runRhythmVerificationAsync` produz o relatório cedendo o
 * controle ao navegador entre as etapas (uma fórmula por vez), o que permite
 * mostrar o progresso SEM apresentar o resultado como aprovado antes do fim.
 */

import { DURATIONS, type DurationId } from '@/lib/music/rhythm';
import {
  buildRhythmExercise,
  emptyRhythmScore,
  isHoldLongEnough,
  minHoldBeats,
  type RhythmConfig,
  type RhythmScore,
} from '@/lib/exercise/rhythmEngine';
import { assessRhythmAttempt } from '@/lib/exercise/rhythmSummary';

const EPS = 1e-9;
/** Limite de mensagens guardadas por balde (a contagem de inválidos não é limitada). */
const MAX_FAILURES = 200;

/**
 * Mínimo de sementes executadas por combinação de cada fórmula. A verificação
 * roda esse valor para CADA combinação (barras × figuras × notas), então cada
 * compasso executa muito mais do que 120 casos. O mínimo exigido por compasso é
 * 120 sementes por combinação.
 */
export const MIN_SEEDS_PER_MEASURE = 120;

export interface SignatureReport {
  beatsPerMeasure: number;
  /** sementes por combinação efetivamente executadas para esta fórmula */
  seedsPerCombination: number;
  /** quantas combinações (barras × figuras × notas) foram testadas */
  combinations: number;
  generated: number;
  invalid: number;
  failures: string[];
}

export interface MeasureSumsReport {
  seedsPerCombination: number;
  generated: number;
  invalid: number;
  failures: string[];
  bySignature: SignatureReport[];
}

export interface ScenarioReport {
  seedsPerCombination: number;
  generated: number;
  matched: number;
  invalid: number;
  failures: string[];
}

export interface VerificationReport {
  measureSums: MeasureSumsReport;
  lastRest: ScenarioReport;
  lastTie: ScenarioReport;
  /** casos inválidos somando todos os cenários */
  invalidTotal: number;
  ok: boolean;
}

export interface VerificationProgress {
  stage: string;
  completed: number;
  total: number;
}

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < EPS;
}

function pushFailure(bucket: string[], message: string): void {
  if (bucket.length < MAX_FAILURES) bucket.push(message);
}

/**
 * Confere um exercício: soma exata por compasso, sem sobreposição nem buraco.
 * Devolve a QUANTIDADE de falhas encontradas (não limitada pelo cap) e copia as
 * mensagens para todos os `collectors` informados.
 */
function checkExercise(config: RhythmConfig, seed: number, collectors: string[][]): number {
  const ex = buildRhythmExercise(config, seed);
  const expectedTotal = config.beatsPerMeasure * config.bars;
  const tag = `[${config.beatsPerMeasure}/4 bars=${config.bars} seed=${seed}]`;
  let count = 0;
  const fail = (message: string): void => {
    count += 1;
    collectors.forEach((bucket) => pushFailure(bucket, message));
  };

  if (!near(ex.totalBeats, expectedTotal)) {
    fail(`${tag} total ${ex.totalBeats} != esperado ${expectedTotal}`);
  }

  let cursor = 0;
  const measureSums = new Array<number>(config.bars).fill(0);

  ex.events.forEach((event, index) => {
    if (!near(event.startBeat, cursor)) {
      fail(`${tag} evento ${index} começa em ${event.startBeat}, esperado ${cursor}`);
    }
    if (event.beats <= 0) {
      fail(`${tag} evento ${index} com duração inválida ${event.beats}`);
    }
    if (!near(DURATIONS[event.durationId].beats, event.beats)) {
      fail(
        `${tag} evento ${index}: figura ${event.durationId} vale ${DURATIONS[event.durationId].beats}, mas o evento declara ${event.beats}`,
      );
    }
    const measure = Math.floor(event.startBeat / config.beatsPerMeasure);
    if (measure >= 0 && measure < measureSums.length) {
      measureSums[measure] += event.beats;
    } else {
      fail(`${tag} evento ${index} fora dos compassos (start ${event.startBeat})`);
    }
    cursor += event.beats;
  });

  if (!near(cursor, ex.totalBeats)) {
    fail(`${tag} soma dos eventos ${cursor} != total ${ex.totalBeats}`);
  }

  measureSums.forEach((sum, index) => {
    if (!near(sum, config.beatsPerMeasure)) {
      fail(`${tag} compasso ${index + 1} soma ${sum}, esperado ${config.beatsPerMeasure}`);
    }
  });

  return count;
}

const NOTE_POOLS: string[][] = [
  ['C4'],
  ['C4', 'D4', 'E4', 'F4', 'G4'],
  ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'],
  ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4', 'B3', 'A3', 'G3', 'C5', 'D5', 'E5'],
];

const FIGURE_SETS: DurationId[][] = [
  ['quarter'],
  ['half'],
  ['whole'],
  ['eighth'],
  ['sixteenth'],
  ['quarter', 'half', 'whole'],
  ['quarter', 'eighth'],
  ['eighth', 'sixteenth'],
  ['dotted-half', 'dotted-quarter', 'quarter', 'eighth'],
  ['quarter', 'eighth', 'sixteenth', 'dotted-quarter'],
];

const SIGNATURES = [2, 3, 4];
const BAR_COUNTS = [1, 2, 4];

/** Combinações testadas por fórmula (barras × figuras × pools de notas). */
const COMBINATIONS_PER_SIGNATURE = BAR_COUNTS.length * FIGURE_SETS.length * NOTE_POOLS.length;

/**
 * Núcleo da verificação em forma de gerador. Cada `yield` representa uma etapa
 * concluída (uma fórmula ou um cenário), permitindo tanto a execução síncrona
 * quanto a execução em etapas no navegador.
 */
function* verifyGenerator(seedsPerCombination: number): Generator<VerificationProgress, VerificationReport> {
  const bySignature: SignatureReport[] = SIGNATURES.map((beatsPerMeasure) => ({
    beatsPerMeasure,
    seedsPerCombination,
    combinations: COMBINATIONS_PER_SIGNATURE,
    generated: 0,
    invalid: 0,
    failures: [],
  }));
  const measureSums: MeasureSumsReport = {
    seedsPerCombination,
    generated: 0,
    invalid: 0,
    failures: [],
    bySignature,
  };

  const totalSignatures = SIGNATURES.length;

  for (let s = 0; s < SIGNATURES.length; s += 1) {
    const beatsPerMeasure = SIGNATURES[s];
    const signatureReport = bySignature[s];

    BAR_COUNTS.forEach((bars) => {
      FIGURE_SETS.forEach((figures) => {
        NOTE_POOLS.forEach((notes) => {
          for (let seed = 0; seed < seedsPerCombination; seed += 1) {
            measureSums.generated += 1;
            signatureReport.generated += 1;
            const config: RhythmConfig = {
              notes,
              figures,
              beatsPerMeasure,
              bpm: 72,
              bars,
              responseMode: 'on-beat',
              exercises: 1,
              countIn: beatsPerMeasure,
            };
            const found = checkExercise(config, seed, [
              measureSums.failures,
              signatureReport.failures,
            ]);
            if (found > 0) {
              measureSums.invalid += 1;
              signatureReport.invalid += 1;
            }
          }
        });
      });
    });

    yield {
      stage: `Compasso ${beatsPerMeasure}/4`,
      completed: s + 1,
      total: totalSignatures,
    };
  }

  const lastRest = verifyLastRest(seedsPerCombination);
  yield { stage: 'Cenário: última pausa', completed: totalSignatures, total: totalSignatures };

  const lastTie = verifyLastTie(seedsPerCombination);
  yield { stage: 'Cenário: última ligadura', completed: totalSignatures, total: totalSignatures };

  const invalidTotal = measureSums.invalid + lastRest.invalid + lastTie.invalid;

  return {
    measureSums,
    lastRest,
    lastTie,
    invalidTotal,
    ok: invalidTotal === 0,
  };
}

/**
 * Roda a verificação de forma síncrona (útil fora do navegador, com `tsx`).
 * Para a página de diagnóstico, prefira `runRhythmVerificationAsync`.
 */
export function runRhythmVerification(seedsPerCombination = MIN_SEEDS_PER_MEASURE): VerificationReport {
  const gen = verifyGenerator(seedsPerCombination);
  let step = gen.next();
  while (!step.done) {
    step = gen.next();
  }
  return step.value as VerificationReport;
}

/**
 * Roda a verificação cedendo o controle ao navegador entre as etapas. O
 * relatório final só existe quando TODAS as sementes rodaram — nunca antes.
 */
export async function runRhythmVerificationAsync(
  seedsPerCombination = MIN_SEEDS_PER_MEASURE,
  onProgress?: (progress: VerificationProgress) => void,
): Promise<VerificationReport> {
  const gen = verifyGenerator(seedsPerCombination);
  let step = gen.next();
  while (!step.done) {
    onProgress?.(step.value as VerificationProgress);
    // Devolve o controle ao navegador para a interface pintar o progresso.
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 0);
    });
    step = gen.next();
  }
  return step.value as VerificationReport;
}

/** Cenário: exercícios com pausas — a soma continua exata e a pausa é válida. */
function verifyLastRest(seeds: number): ScenarioReport {
  const report: ScenarioReport = {
    seedsPerCombination: seeds,
    generated: 0,
    matched: 0,
    invalid: 0,
    failures: [],
  };
  for (let seed = 0; seed < seeds; seed += 1) {
    report.generated += 1;
    const config: RhythmConfig = {
      notes: ['C4', 'D4', 'E4', 'G4'],
      figures: ['quarter', 'half'],
      rests: ['quarter', 'half'],
      restChance: 0.6,
      beatsPerMeasure: 4,
      bpm: 72,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 1,
      countIn: 4,
    };
    const found = checkExercise(config, seed, [report.failures]);
    const ex = buildRhythmExercise(config, seed);
    const last = ex.events[ex.events.length - 1];
    if (last && last.kind === 'rest') report.matched += 1;
    if (found > 0) report.invalid += 1;
  }
  return report;
}

/** Cenário: exercícios com ligadura — a continuação segue a nota-base de mesma altura. */
function verifyLastTie(seeds: number): ScenarioReport {
  const report: ScenarioReport = {
    seedsPerCombination: seeds,
    generated: 0,
    matched: 0,
    invalid: 0,
    failures: [],
  };
  for (let seed = 0; seed < seeds; seed += 1) {
    report.generated += 1;
    const config: RhythmConfig = {
      notes: ['C4', 'D4', 'E4', 'F4', 'G4'],
      figures: ['quarter', 'half'],
      beatsPerMeasure: 4,
      bpm: 64,
      bars: 2,
      responseMode: 'on-beat',
      exercises: 1,
      countIn: 4,
      ties: true,
    };
    const found = checkExercise(config, seed, [report.failures]);
    let extra = 0;
    const ex = buildRhythmExercise(config, seed);
    const tieIndex = ex.events.findIndex((event) => event.kind === 'note' && event.tied === true);
    if (tieIndex > 0) {
      report.matched += 1;
      const base = ex.events[tieIndex - 1];
      const tie = ex.events[tieIndex];
      const samePitch = base.kind === 'note' && tie.kind === 'note' && base.noteId === tie.noteId;
      if (!samePitch) {
        extra += 1;
        pushFailure(report.failures, `seed=${seed}: ligadura sem nota-base de mesma altura`);
      }
      const endsAtBoundary =
        (Math.round(base.startBeat * 4) + Math.round(base.beats * 4)) %
          Math.round(4 * config.beatsPerMeasure) ===
        0;
      if (!endsAtBoundary) {
        extra += 1;
        pushFailure(report.failures, `seed=${seed}: ligadura não cruza a barra do compasso`);
      }
    }
    if (found > 0 || extra > 0) report.invalid += 1;
  }
  return report;
}

/** Um caso reproduzível da regra de desbloqueio / de duração. */
export interface RuleCase {
  id: string;
  title: string;
  expected: string;
  actual: string;
  passed: boolean;
}

export interface RuleReport {
  cases: RuleCase[];
  failed: number;
  ok: boolean;
}

/** Placar base de uma execução PERFEITA (todos os itens exigidos sem erro). */
function perfectScore(): RhythmScore {
  return {
    ...emptyRhythmScore(),
    correct: 4,
    restsHeld: 1,
    tiesHeld: 1,
    durationsHeld: 4,
    noteOffCount: 4,
    attempts: 4,
  };
}

const FULL_CONTEXT = { hasRests: true, hasTies: true, measuresDuration: true };

function caseOf(
  id: string,
  title: string,
  expected: string,
  actual: string,
  passed: boolean,
): RuleCase {
  return { id, title, expected, actual, passed };
}

/** Testa a regra de duração (segurar até perto do fim, com tolerância explícita). */
function durationCases(): RuleCase[] {
  return [
    caseOf(
      'dur-half-early',
      'Mínima (2 pulsos) solta em 1 pulso',
      'ERRO',
      isHoldLongEnough(1, 2) ? 'certo' : 'ERRO',
      isHoldLongEnough(1, 2) === false,
    ),
    caseOf(
      'dur-half-near',
      'Mínima (2 pulsos) segurada quase até o fim (1,75)',
      'CERTO',
      isHoldLongEnough(1.75, 2) ? 'CERTO' : 'erro',
      isHoldLongEnough(1.75, 2) === true,
    ),
    caseOf(
      'dur-whole-early',
      'Semibreve (4 pulsos) solta em 3 pulsos',
      'ERRO',
      isHoldLongEnough(3, 4) ? 'certo' : 'ERRO',
      isHoldLongEnough(3, 4) === false,
    ),
    caseOf(
      'dur-whole-near',
      'Semibreve (4 pulsos) segurada até 3,75',
      'CERTO',
      isHoldLongEnough(3.75, 4) ? 'CERTO' : 'erro',
      isHoldLongEnough(3.75, 4) === true,
    ),
    caseOf(
      'dur-sixteenth',
      'Semicolcheia (0,25) segurada até 0,20',
      'CERTO',
      isHoldLongEnough(0.2, 0.25) ? 'CERTO' : 'erro',
      isHoldLongEnough(0.2, 0.25) === true,
    ),
    caseOf(
      'dur-min-rule',
      'Exigência da mínima = 1,75 pulso',
      '1.75',
      String(minHoldBeats(2)),
      Math.abs(minHoldBeats(2) - 1.75) < 1e-9,
    ),
  ];
}

/** Testa o critério de desbloqueio (execução sem erros por item). */
function masteryCases(): RuleCase[] {
  const cases: RuleCase[] = [];

  const perfect = assessRhythmAttempt(perfectScore(), FULL_CONTEXT);
  cases.push(
    caseOf(
      'master-perfect',
      'Execução completa sem erros (nota, ataque, duração, pausa, ligadura)',
      'Aula dominada',
      perfect.mastered ? 'Aula dominada' : 'NÃO domina',
      perfect.mastered === true,
    ),
  );

  const wrongPitch = assessRhythmAttempt({ ...perfectScore(), wrongNote: 1 }, FULL_CONTEXT);
  cases.push(
    caseOf('fail-note', 'Errar a altura da nota', 'NÃO domina', wrongPitch.mastered ? 'domina' : 'NÃO domina', wrongPitch.mastered === false),
  );

  const missed = assessRhythmAttempt(
    { ...perfectScore(), correct: 3, durationsHeld: 3, noteOffCount: 3, missedNotes: 1 },
    FULL_CONTEXT,
  );
  cases.push(
    caseOf('fail-missed', 'Não tocar uma nota escrita', 'NÃO domina', missed.mastered ? 'domina' : 'NÃO domina', missed.mastered === false),
  );

  const lateAttack = assessRhythmAttempt({ ...perfectScore(), correct: 3, late: 1 }, FULL_CONTEXT);
  cases.push(
    caseOf('fail-attack', 'Atacar fora do pulso (atrasado)', 'NÃO domina', lateAttack.mastered ? 'domina' : 'NÃO domina', lateAttack.mastered === false),
  );

  const restHit = assessRhythmAttempt({ ...perfectScore(), restIntrusion: 1 }, FULL_CONTEXT);
  cases.push(
    caseOf(
      'fail-rest',
      'Tocar em cima da pausa (inclusive a última do exercício)',
      'NÃO domina',
      restHit.mastered ? 'domina' : 'NÃO domina',
      restHit.mastered === false,
    ),
  );

  const tieHit = assessRhythmAttempt({ ...perfectScore(), tieReattack: 1 }, FULL_CONTEXT);
  cases.push(
    caseOf(
      'fail-tie',
      'Reatacar a ligadura (inclusive a última do exercício)',
      'NÃO domina',
      tieHit.mastered ? 'domina' : 'NÃO domina',
      tieHit.mastered === false,
    ),
  );

  const shortHold = assessRhythmAttempt({ ...perfectScore(), durationsHeld: 3, durationBreak: 1 }, FULL_CONTEXT);
  cases.push(
    caseOf('fail-duration', 'Soltar a nota antes do fim da figura', 'NÃO domina', shortHold.mastered ? 'domina' : 'NÃO domina', shortHold.mastered === false),
  );

  // Última nota segurada até o fim, com o aparelho confirmando o fim da nota.
  const lastNoteOk = assessRhythmAttempt(
    { ...perfectScore(), correct: 1, durationsHeld: 0, heldToEnd: 1, restsHeld: 0, tiesHeld: 0, noteOffCount: 1, attempts: 1 },
    FULL_CONTEXT,
  );
  cases.push(
    caseOf(
      'last-note-ok',
      'Última nota segurada até o fim (com note-off)',
      'conta como certa',
      lastNoteOk.items.find((i) => i.key === 'duration')?.wrong === 0 ? 'conta como certa' : 'conta como erro',
      (lastNoteOk.items.find((i) => i.key === 'duration')?.wrong ?? 1) === 0,
    ),
  );

  // Última pausa respeitada.
  const lastRestOk = assessRhythmAttempt(
    { ...perfectScore(), correct: 1, durationsHeld: 0, restsHeld: 1, tiesHeld: 0, noteOffCount: 1, attempts: 1 },
    FULL_CONTEXT,
  );
  cases.push(
    caseOf(
      'last-rest-ok',
      'Última pausa respeitada (silêncio)',
      'conta como certa',
      (lastRestOk.items.find((i) => i.key === 'rest')?.wrong ?? 1) === 0 ? 'conta como certa' : 'conta como erro',
      (lastRestOk.items.find((i) => i.key === 'rest')?.wrong ?? 1) === 0,
    ),
  );

  // Última ligadura mantida.
  const lastTieOk = assessRhythmAttempt(
    { ...perfectScore(), correct: 1, durationsHeld: 0, restsHeld: 0, tiesHeld: 1, noteOffCount: 1, attempts: 1 },
    FULL_CONTEXT,
  );
  cases.push(
    caseOf(
      'last-tie-ok',
      'Última ligadura mantida (um só ataque)',
      'conta como certa',
      (lastTieOk.items.find((i) => i.key === 'tie')?.wrong ?? 1) === 0 ? 'conta como certa' : 'conta como erro',
      (lastTieOk.items.find((i) => i.key === 'tie')?.wrong ?? 1) === 0,
    ),
  );

  // Duração sem note-off: fica NÃO AVALIADA — sem erros, mas não dominada.
  const unmeasured = assessRhythmAttempt(
    { ...perfectScore(), correct: 1, durationsHeld: 0, heldToEnd: 1, restsHeld: 0, tiesHeld: 0, noteOffCount: 0, attempts: 1 },
    FULL_CONTEXT,
  );
  cases.push(
    caseOf(
      'dur-unmeasured',
      'Duração sem o aparelho informar o fim da nota',
      'não avaliada (errorFree mas não dominada)',
      unmeasured.notEvaluatedKeys.includes('duration')
        ? unmeasured.mastered
          ? 'dominada'
          : 'não avaliada'
        : 'avaliada',
      unmeasured.notEvaluatedKeys.includes('duration') === true && unmeasured.mastered === false,
    ),
  );

  return cases;
}

/**
 * Casos reproduzíveis e determinísticos da REGRA DE DESBLOQUEIO e da REGRA DE
 * DURAÇÃO. É puro: roda no navegador (página /diagnostico) ou fora dele.
 */
export function runRhythmRuleChecks(): RuleReport {
  const cases = [...durationCases(), ...masteryCases()];
  const failed = cases.filter((item) => !item.passed).length;
  return { cases, failed, ok: failed === 0 };
}